import React from "react";

/**
 * The native-WebSocket replacement for socket.io (rust-rewrite phase 7).
 *
 * The server speaks a JSON envelope over `/ws`:
 *   outbound: `{event: "user" | "queue" | "completed", data}`
 *   inbound:  `{event: "status"}`
 *
 * Auth rides the session cookie on the upgrade request. Reconnects use a
 * capped linear backoff; `onOpen` re-fires on each (re)connect so callers
 * can re-request state.
 */

export interface AppSocket {
  send(event: string): void;
}

export default function useAppSocket(
  onOpen: (socket: AppSocket) => void,
  onEvent: (event: string, data: unknown) => void,
  changeTriggers: unknown[]
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openCallback = React.useCallback(
    onOpen,
    changeTriggers as React.DependencyList
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const eventCallback = React.useCallback(
    onEvent,
    changeTriggers as React.DependencyList
  );

  React.useEffect(() => {
    let socket: WebSocket | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    let closed = false;

    function connect() {
      const base = process.env.PUBLIC_URL || window.location.origin;
      const url = new URL("/ws", base);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(url.toString());

      const appSocket: AppSocket = {
        send(event: string) {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ event }));
          }
        },
      };

      socket.onopen = () => {
        attempts = 0;
        openCallback(appSocket);
      };

      socket.onmessage = (message: MessageEvent) => {
        try {
          const { event, data } = JSON.parse(message.data);
          if (typeof event === "string") eventCallback(event, data);
        } catch {
          // ignore malformed frames
        }
      };

      socket.onclose = () => {
        if (closed) return;
        attempts += 1;
        reconnectTimer = setTimeout(connect, Math.min(1000 * attempts, 10000));
      };
    }

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [openCallback, eventCallback]);
}
